import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { clearPlatformSession, getCurrentPlatformAuthMode, getCurrentPlatformSession, isSingleSignOutEnabled } from '@auth/platformSession';
import SystemStore from '../stores/SystemStore';
import WebClientStore from '../stores/WebClientStore';
import PreferencesStore from '../stores/PreferencesStore';
import SystemActions from '../actions/SystemActions';
import ClipboardHelper from '../common/ClipboardHelper';
import PermissionService from '../services/PermissionService';
import UserProfileApp from '../components/UserProfile/components/UserProfileAppReadOnly';
import UserLocaleApp from '../components/UserLocale/components/UserLocaleApp';
import { getFrontendConfig } from '../../config/frontendConfig';
import AboutDialog from '../components/About/AboutDialog';
import { HELP_ENABLED } from '@facility/constants/ReleaseFeatureFlags';

type ActivePopup = 'about' | 'account' | 'locale' | 'preferences' | 'userSystemPreferences' | null;
type OpenDropdown = 'help' | 'share' | null;

const AppHeader: React.FC = () => {
    const { instance, accounts } = useMsal();
    const userName = getCurrentPlatformSession()?.User?.DisplayName || accounts[0]?.name || accounts[0]?.username || '';

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activePopup, setActivePopup] = useState<ActivePopup>(null);
    const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null);
    const [exportEnabled, setExportEnabled] = useState(false);
    const [hasPreferences, setHasPreferences] = useState(false);
    const [hasUserSystemPrefs, setHasUserSystemPrefs] = useState(false);
    const [preferenceAppName, setPreferenceAppName] = useState('');
    const helpTriggerRef = useRef<HTMLAnchorElement>(null);

    const updateExportState = useCallback(() => {
        setExportEnabled(
            WebClientStore.canExportScene() &&
            PermissionService.checkPermission('KnowledgeBook.ExportScene')
        );
    }, []);

    const updatePreferencesState = useCallback(() => {
        setHasPreferences(PreferencesStore.hasActivePreferences());
        setHasUserSystemPrefs(PreferencesStore.hasUserSystemPrefrences());
        setPreferenceAppName(PreferencesStore.getApplicationName() || '');
    }, []);

    useEffect(() => {
        updateExportState();
        updatePreferencesState();
        WebClientStore.addChangeListener(updateExportState);
        PreferencesStore.addChangeListener(updatePreferencesState);
        return () => {
            WebClientStore.removeChangeListener(updateExportState);
            PreferencesStore.removeChangeListener(updatePreferencesState);
        };
    }, [updateExportState, updatePreferencesState]);

    // Close dropdowns on outside click; close sidebar if no popup is active
    useEffect(() => {
        const handleDocClick = () => {
            setOpenDropdown(null);
            setActivePopup(prev => {
                if (prev === null) setSidebarOpen(false);
                return prev;
            });
        };
        document.addEventListener('click', handleDocClick);
        return () => document.removeEventListener('click', handleDocClick);
    }, []);

    const handleSignOut = async () => {
        const basePath = getFrontendConfig().urls.basePath;
        const postLogoutRedirectUri = new URL(basePath, window.location.origin).toString();
        const authMode = getCurrentPlatformAuthMode();
        const singleSignOut = isSingleSignOutEnabled();

        clearPlatformSession();

        if (authMode === 'local') {
            window.location.href = basePath;
            return;
        }

        if (singleSignOut) {
            await instance.logoutRedirect({
                postLogoutRedirectUri,
            });

            return;
        }

        await instance.clearCache();
        window.location.href = basePath;
    };

    const handleToggleDropdown = (e: React.MouseEvent, dropdown: OpenDropdown) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenDropdown(prev => prev === dropdown ? null : dropdown);
    };

    const handleCopyLink = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!SystemStore.getIsShareButtonDisabled()) {
            ClipboardHelper.copyToClipboard(location.href);
        }
        setOpenDropdown(null);
    };

    const handleEmailLink = (e: React.MouseEvent) => {
        if (SystemStore.getIsShareButtonDisabled()) {
            e.preventDefault();
        }
        setOpenDropdown(null);
    };

    const handleExportGLB = (e: React.MouseEvent) => {
        e.stopPropagation();
        WebClientStore.exportSceneToGLB();
        setOpenDropdown(null);
    };

    const handleExportOBJ = (e: React.MouseEvent) => {
        e.stopPropagation();
        WebClientStore.exportSceneToOBJ();
        setOpenDropdown(null);
    };

    const handlePrint = (e: React.MouseEvent) => {
        e.stopPropagation();
        SystemActions.PublicActions.print();
        setOpenDropdown(null);
    };

    const openSidebar = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSidebarOpen(true);
    };

    const closeSidebar = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSidebarOpen(false);
    };

    const openAccountPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActivePopup('account');
    };

    const openLocalePopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActivePopup('locale');
    };

    const openPreferencesPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActivePopup('preferences');
    };

    const openUserSystemPrefsPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActivePopup('userSystemPreferences');
    };

    const openAboutPopup = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenDropdown(null);
        setActivePopup('about');
    };

    const closePopup = () => {
        setActivePopup(null);
    };

    const closeAboutPopup = useCallback(() => {
        setActivePopup(null);
        window.requestAnimationFrame(() => helpTriggerRef.current?.focus());
    }, []);

    const canManage = PermissionService.checkPermission('Facility.Manage');
    const emailHref = `mailto:?subject=${encodeURIComponent(InovxLoader.t('common:header.knowledgeViewLink'))}&body=${encodeURIComponent(location.href)}`;

    const renderPopupContent = () => {
        switch (activePopup) {
            case 'account':
                return <UserProfileApp onOK={closePopup} />;
            case 'locale':
                return <UserLocaleApp onClose={closePopup} />;
            case 'preferences':
                return PreferencesStore.hasActivePreferences()
                    ? PreferencesStore.displayActivePreferences({ onClose: closePopup })
                    : null;
            case 'userSystemPreferences':
                return PreferencesStore.hasUserSystemPrefrences()
                    ? PreferencesStore.displayUserSystemPreferences({ onClose: closePopup })
                    : null;
            default:
                return null;
        }
    };

    return (
        <>
            {/* ── Top navigation bar ───────────────────────────────────── */}
            <nav className="navbar top-header" role="navigation">
                <div className="container-fluid ihdr-main">

                    {/* Left: Logo + divider + app name */}
                    <div className="navbar-header ihdr-logo">
                        <a className="navbar-brand" href="/" id="vsuite-home">
                            <img className="img-responsive" src="/icons/visionaize-logo.png" alt="Visionaize" />
                        </a>
                        <div className="divider-vertical"></div>
                        <h3 className="navbar-text">
                            <a className="ihdr-home-link" href="/">V-Plant</a>
                        </h3>
                    </div>

                    {/* Center: dynamic page title (set by FacilityApp / viewers via DOM id) */}
                    <div className="nav-app-title-container" id="ihdr-title-container">
                        <ul className="nav navbar-nav">
                            <li className="ihdr-menu-list">
                                <h3 className="nav-app-title" id="nav-app-title"></h3>
                            </li>
                        </ul>
                    </div>

                    {/* Right: Help / Share / User */}
                    <div className="collapse navbar-collapse ihdr-menu" id="navbar-collapse">
                        <ul className="nav navbar-nav navbar-right">

                            {/* User System Preferences gear (shown when a system prefs component is registered) */}
                            {hasUserSystemPrefs && (
                                <li className="ihdr-menu-list" id="user-system-preferences">
                                    <a id="ihdr-user-system-preferences" onClick={openUserSystemPrefsPopup}>
                                        <span className="glyphicon glyphicon-cog"></span>
                                    </a>
                                </li>
                            )}

                            {/* Help dropdown */}
                            <li className="ihdr-menu-list">
                                <a ref={helpTriggerRef} href="#" className="ihdr-menu-toggle ihdr-help" id="ihdr-help"
                                    onClick={(e) => handleToggleDropdown(e, 'help')}>
                                    <img src="/icons/ihdr-help.png" alt="" />{InovxLoader.t('common:header.help')}
                                </a>
                                <ul className={`ihdr-menu-toggle-child${openDropdown === 'help' ? ' ihdr-dropdown-open' : ''}`}>
                                    {canManage && (
                                        <li>
                                            <a id="ihdr-help-download-vsuiteconnect"
                                                href="https://help.visionaize.com/connect/5.0/VPlantConnectSetup.exe"
                                                target="_self" download>
                                                {InovxLoader.t('common:header.downloadVPlantConnect')}
                                            </a>
                                        </li>
                                    )}
                                    {HELP_ENABLED && (
                                        <li>
                                            <a id="ihdr-help-for-page" href="#" target="_blank" rel="noopener noreferrer">
                                                {InovxLoader.t('common:header.helpForThisPage')}
                                            </a>
                                        </li>
                                    )}
                                    {HELP_ENABLED && (
                                        <li>
                                            <a href="https://help.visionaize.com/ContactUs.html" target="_blank" rel="noopener noreferrer">
                                                {InovxLoader.t('common:header.contactUs')}
                                            </a>
                                        </li>
                                    )}
                                    <li>
                                        <a href="#" onClick={openAboutPopup}>
                                            {InovxLoader.t('common:header.about')}
                                        </a>
                                    </li>
                                </ul>
                            </li>

                            {/* Share dropdown */}
                            <li className="ihdr-menu-list">
                                <a id="ihdr-share" className="ihdr-menu-toggle"
                                    onClick={(e) => handleToggleDropdown(e, 'share')}>
                                    <img src="/icons/ihdr-share.png" alt="" />{InovxLoader.t('common:header.share')}
                                </a>
                                <ul className={`ihdr-menu-toggle-child${openDropdown === 'share' ? ' ihdr-dropdown-open' : ''}`} id="ihdr-share-list">
                                    <li>
                                        <a id="ihdr-share-copy" onClick={handleCopyLink}>
                                            <span className="glyphicon glyphicon-link"></span>{InovxLoader.t('common:header.copyLink')}
                                        </a>
                                    </li>
                                    <li>
                                        <a id="ihdr-share-email" href={emailHref} target="_blank"
                                            rel="noopener noreferrer" onClick={handleEmailLink}>
                                            <span className="glyphicon glyphicon-envelope"></span>{InovxLoader.t('common:header.emailLink')}
                                        </a>
                                    </li>
                                    {exportEnabled && (
                                        <>
                                            <li>
                                                <a id="ihdr-share-export-glb" onClick={handleExportGLB}>
                                                    <span className="glyphicon glyphicon-export"></span>{InovxLoader.t('common:header.exportSceneToGLB')}
                                                </a>
                                            </li>
                                            <li>
                                                <a id="ihdr-share-export-obj" onClick={handleExportOBJ}>
                                                    <span className="glyphicon glyphicon-export"></span>{InovxLoader.t('common:header.exportSceneToOBJ')}
                                                </a>
                                            </li>
                                        </>
                                    )}
                                    <li>
                                        <a id="ihdr-print" onClick={handlePrint}>
                                            <span className="glyphicon glyphicon-print"></span>{InovxLoader.t('common:header.print')}
                                        </a>
                                    </li>
                                </ul>
                            </li>

                            {/* User account button → opens slide-out sidebar */}
                            <li className="ihdr-menu-list">
                                <a id="ihdr-user-account" onClick={openSidebar}>
                                    <img src="/icons/ihdr-user.png" alt="" />{userName}
                                </a>
                            </li>

                        </ul>
                    </div>
                </div>
            </nav>

            {/* ── Account slide-out sidebar ────────────────────────────── */}
            <div className={`cd-panel from-right${sidebarOpen ? ' is-visible' : ''}`} id="account-sidebar">
                <div className="cd-panel-container cd-panel-account">
                    <div className="cd-panel-content">
                        <div className="panel panel-primary fc-expand-last" id="account-sidebar-panel"
                            onClick={(e) => e.stopPropagation()}>
                            <div className="panel-primary toolbar-header">
                                <div className="panel-heading">
                                    {userName}
                                    <button type="button" className="close" id="account-sidebar-close-button"
                                        aria-label={InovxLoader.t('common:header.close')} onClick={closeSidebar}>
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                            </div>
                            <div className="ctnt-main fc-margin">
                                <div className="ihdr-menu-list">
                                    <a id="show-myaccount-popup-button" onClick={openAccountPopup}>
                                        <span className="glyphicon glyphicon-user"></span>&nbsp;{InovxLoader.t('common:header.myAccount')}
                                    </a>
                                    <a onClick={handleSignOut}>
                                        <span className="glyphicon glyphicon-log-out"></span>&nbsp;{InovxLoader.t('common:header.signOut')}
                                    </a>
                                    <div className="panel-menu-section" id="LocaleMenu">
                                        <hr />
                                        <h5>{InovxLoader.t('common:header.locale')}</h5>
                                        <a id="show-mylocale-popup-button" onClick={openLocalePopup}>
                                            <span className="glyphicon glyphicon-cog"></span>&nbsp;{InovxLoader.t('common:header.myPreferences')}
                                        </a>
                                    </div>
                                    {hasPreferences && (
                                        <div className="panel-menu-section" id="KnowledgeBookMenu">
                                            <hr />
                                            <h5 id="preferences-app-title">
                                                {preferenceAppName || InovxLoader.t('common:header.knowledgeBook')}
                                            </h5>
                                            <a id="show-mypreferences-popup-button" onClick={openPreferencesPopup}>
                                                <span className="glyphicon glyphicon-cog"></span>&nbsp;{InovxLoader.t('common:header.myPreferences')}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Modal popup (account / preferences / user-system-prefs) ─ */}
            {(activePopup === 'account' || activePopup === 'preferences' || activePopup === 'userSystemPreferences') && createPortal(
                <div className="cd-panel-modal faded is-visible" id="account-popup" onClick={closePopup}>
                    <div className="cd-panel-modal-middle" onClick={(e) => e.stopPropagation()}>
                        <div className="cd-panel-modal-inner">
                            {renderPopupContent()}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Modal popup (locale) ─────────────────────────────────── */}
            {activePopup === 'locale' && createPortal(
                <div className="cd-panel-modal faded is-visible" id="locale-popup" onClick={closePopup}>
                    <div className="cd-panel-modal-middle" onClick={(e) => e.stopPropagation()}>
                        <div className="cd-panel-modal-inner">
                            {renderPopupContent()}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── About modal ─────────────────────────────────────────── */}
            {activePopup === 'about' && createPortal(
                <div className="cd-panel-modal faded is-visible about-modal" onClick={closeAboutPopup}>
                    <div className="cd-panel-modal-middle">
                        <div className="cd-panel-modal-inner about-modal-inner" onClick={(e) => e.stopPropagation()}>
                            <AboutDialog onClose={closeAboutPopup} />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default AppHeader;
